import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { playClip, unlockAudio } from "../../lib/audio";

type Assoc = {
  id: number;
  song_id: string;
  theme_id: number;
  theme_name: string;
  canonical_answer: string;
  justification: string;
  status: "pending" | "approved" | "rejected";
  confidence: number;
  title?: string;
  artist?: string;
  origin: string;
};

type ThemeRow = { id: number; name: string; active: number | boolean };

type StatusFilter = "all" | "pending" | "approved" | "rejected";

export default function AdminHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [ok, setOk] = useState(false);
  const [list, setList] = useState<Assoc[]>([]);
  const [themes, setThemes] = useState<ThemeRow[]>([]);
  const [jobs, setJobs] = useState<Array<{ id: number; status: string; processed: number; total: number }>>([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState<number | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);

  useEffect(() => {
    void api("/api/admin/me")
      .then(() => {
        setOk(true);
        return reload();
      })
      .catch(() => nav("/admin/login"));
  }, [nav]);

  async function reload() {
    const [a, t, j] = await Promise.all([
      api<Assoc[]>("/api/admin/associations"),
      api<ThemeRow[]>("/api/admin/themes"),
      api<Array<{ id: number; status: string; processed: number; total: number }>>("/api/admin/jobs"),
    ]);
    setList(
      a.map((row) => ({
        ...row,
        theme_id: Number(row.theme_id),
        status: row.status,
      })),
    );
    setThemes(t);
    setJobs(j);
  }

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list
      .filter((row) => (filter === "all" ? true : row.status === filter))
      .filter((row) => {
        if (!needle) return true;
        return `${row.title ?? ""} ${row.artist ?? ""} ${row.canonical_answer} ${row.theme_name}`.toLowerCase().includes(needle);
      })
      .sort((a, b) => `${a.artist ?? ""} ${a.title ?? ""}`.localeCompare(`${b.artist ?? ""} ${b.title ?? ""}`, "fr"));
  }, [list, q, filter]);

  async function patch(id: number, body: Record<string, unknown>, optimistic: (row: Assoc) => Assoc) {
    setErr("");
    const previous = list;
    setList((rows) => rows.map((row) => (row.id === id ? optimistic(row) : row)));
    setSaving(id);
    try {
      await api(`/api/admin/associations/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    } catch (e) {
      setList(previous);
      setErr(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setSaving(null);
    }
  }

  async function listen(row: Assoc) {
    setErr("");
    setPlayingId(row.id);
    try {
      await unlockAudio();
      const clip = await api<{ url: string; clipStartSeconds: number }>(
        `/api/admin/songs/${encodeURIComponent(row.song_id)}/audio`,
      );
      await playClip(clip.url, clip.clipStartSeconds);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Lecture impossible");
    } finally {
      setPlayingId(null);
    }
  }

  async function runAnalyze() {
    await api("/api/admin/analyze", { method: "POST", body: "{}" });
    await reload();
  }

  if (!ok) return null;

  return (
    <div className="grid gap-5 pt-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl">Atelier éditorial</h1>
          <p className="opacity-80 mt-1">
            {visible.length} / {list.length} chansons
            {user ? ` · ${user.username}` : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="rounded-2xl bg-mint text-ink px-4 py-2" onClick={() => void runAnalyze()}>
            Lancer l’analyse
          </button>
          <a className="rounded-2xl border px-4 py-2" href="/api/admin/export">
            Export JSON
          </a>
          <a className="rounded-2xl border px-4 py-2" href="/api/admin/export?format=csv">
            Export CSV
          </a>
          <Link className="rounded-2xl border px-4 py-2" to="/">
            Retour au jeu
          </Link>
        </div>
      </div>
      {jobs[0] && (
        <p className="text-sm opacity-80">
          Dernier job #{jobs[0].id} {jobs[0].status} {jobs[0].processed}/{jobs[0].total}
        </p>
      )}
      {err && <p className="text-pink">{err}</p>}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          className="rounded-2xl bg-cream text-ink px-4 py-2 min-w-[16rem] flex-1"
          placeholder="Filtrer nom, artiste, mot, thème…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {(["all", "pending", "approved", "rejected"] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`rounded-full px-3 py-1 text-sm border ${filter === s ? "bg-gold text-ink border-gold" : "border-cream/30"}`}
            onClick={() => setFilter(s)}
          >
            {s === "all" ? "Tout" : s === "pending" ? "En attente" : s === "approved" ? "Approuvées" : "Refusées"}
          </button>
        ))}
      </div>
      <div className="overflow-auto max-h-[70dvh] rounded-2xl border border-cream/15">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Artiste</th>
              <th>Mot retenu</th>
              <th>Thème</th>
              <th className="text-center">Approuver</th>
              <th className="text-center">Refuser</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} className={row.status === "rejected" ? "opacity-60" : undefined}>
                <td className="font-semibold">{row.title || "—"}</td>
                <td>{row.artist || "—"}</td>
                <td>
                  <input
                    className="w-full min-w-[8rem] rounded-xl bg-cream text-ink px-2 py-1"
                    defaultValue={row.canonical_answer}
                    key={`${row.id}-${row.canonical_answer}`}
                    disabled={saving === row.id}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (!next || next === row.canonical_answer) {
                        e.target.value = row.canonical_answer;
                        return;
                      }
                      void patch(row.id, { canonicalAnswer: next }, (cur) => ({ ...cur, canonical_answer: next }));
                    }}
                  />
                </td>
                <td>
                  <select
                    className="w-full min-w-[9rem] rounded-xl bg-cream text-ink px-2 py-1"
                    value={row.theme_id}
                    disabled={saving === row.id}
                    onChange={(e) => {
                      const themeId = Number(e.target.value);
                      const theme = themes.find((t) => t.id === themeId);
                      void patch(row.id, { themeId }, (cur) => ({
                        ...cur,
                        theme_id: themeId,
                        theme_name: theme?.name ?? cur.theme_name,
                      }));
                    }}
                  >
                    {themes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="admin-check"
                    checked={row.status === "approved"}
                    disabled={saving === row.id}
                    aria-label={`Approuver ${row.title ?? row.id}`}
                    onChange={(e) => {
                      const status = e.target.checked ? "approved" : "pending";
                      void patch(row.id, { status }, (cur) => ({ ...cur, status }));
                    }}
                  />
                </td>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="admin-check"
                    checked={row.status === "rejected"}
                    disabled={saving === row.id}
                    aria-label={`Refuser ${row.title ?? row.id}`}
                    onChange={(e) => {
                      const status = e.target.checked ? "rejected" : "pending";
                      void patch(row.id, { status }, (cur) => ({ ...cur, status }));
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="text-gold text-sm whitespace-nowrap"
                    onClick={() => void listen(row)}
                  >
                    {playingId === row.id ? "…" : "Écouter"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && <p className="p-6 opacity-70">Aucune chanson pour ce filtre.</p>}
      </div>
    </div>
  );
}
